import { Button, Loader, Stack, Text, Textarea } from '@mantine/core'
import { IconDeviceMobile, IconDeviceTablet } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import type { ScreenAsk, Task, TaskKind } from '@tnn/domain'
import { taskWaiting } from '@tnn/domain'
import { RemoteSelect, ResponsiveDialog } from '@tnn/ui'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isApiError } from '../api/client'
import { useOptionalSession } from '../auth/session'
import { useMyScreen } from '../screen/screen'
import { tasksApi, tasksKey, useTask } from './api'
import classes from './handoff.module.css'

/** The record a form is for (ResourceForm): a task from one of its fields goes on the record itself once it is saved */
export interface TaskTarget {
  resource: string
  /** Null while the record is new: the task then fills the open form only */
  recordId: string | null
  /** What the doer sees it is for: "ใบรับเงิน RV6910-0001" */
  title: string
}

const TaskTargetContext = createContext<TaskTarget | null>(null)
export const TaskTargetProvider = TaskTargetContext.Provider
export const useTaskTarget = () => useContext(TaskTargetContext)

interface HandoffOptions {
  kind: TaskKind
  /** The field as the form shows it */
  label: string
  /** The record's field it fills, for a saved record (the task then puts it there itself) */
  field?: string
  /** What was handed in, once */
  onDone: (task: Task) => void
}

/**
 * A field done on another device (`taskKinds` in @tnn/domain): asks for it — on one's own portable screen, or of
 * someone on their phone — watches the task, and hands in what was done once. A task with nowhere to go but this form
 * is taken back when the field leaves the screen. Only for someone signed in (`available`).
 */
export function useHandoff({ kind, label, field, onDone }: HandoffOptions) {
  const session = useOptionalSession()
  const target = useTaskTarget()
  const client = useQueryClient()
  const [taskId, setTaskId] = useState<string | null>(null)
  const task = useTask(taskId).data
  const done = useRef(onDone)
  done.current = onDone
  const handed = useRef<string | null>(null)
  useEffect(() => {
    if (task?.status !== 'done' || handed.current === task.id) return
    handed.current = task.id
    done.current(task)
    setTaskId(null)
  }, [task])

  // Leaving the screen with one still waiting that only this form would take: take it back
  const waiting = useRef<{ id: string; placed: boolean } | null>(null)
  waiting.current = task && taskWaiting(task.status) ? { id: task.id, placed: !!task.target } : null
  useEffect(
    () => () => {
      const left = waiting.current
      if (left && !left.placed) tasksApi.cancel(left.id).catch(() => {})
    },
    [],
  )

  /** `ask`: on one's own screen, what someone at the counter signs to */
  const ask = useCallback(
    async (assigneeId: string, note?: string, screenAsk?: ScreenAsk) => {
      const placed = target?.recordId && field ? { resource: target.resource, recordId: target.recordId, field } : null
      const created = await tasksApi.create({
        kind,
        assigneeId,
        label,
        title: target?.title || null,
        note: note?.trim() || null,
        target: placed,
        ask: screenAsk ?? null,
      })
      client.setQueryData([...tasksKey, created.id], created)
      setTaskId(created.id)
      return created
    },
    [client, kind, label, field, target],
  )
  const cancel = useCallback(async () => {
    if (taskId) await tasksApi.cancel(taskId).catch(() => {})
    setTaskId(null)
  }, [taskId])

  return {
    available: !!session,
    myId: session?.me.user.id ?? null,
    task: taskId ? task : undefined,
    ask,
    cancel,
    clear: () => setTaskId(null),
  }
}

export type Handoff = ReturnType<typeof useHandoff>

/**
 * Where a field stands while it waits: on one's screen (open there or not), for someone (waiting to be taken), or why
 * it ended; done here instead, cancelled, or dismissed
 */
export function HandoffStatus({ handoff }: { handoff: Handoff }) {
  const { t } = useTranslation()
  const screen = useMyScreen().data
  const task = handoff.task
  if (!task) return null
  const own = task.assignee.id === handoff.myId
  const name = task.assignee.name
  const waiting = taskWaiting(task.status)
  const text =
    task.status === 'open'
      ? own
        ? t('screen.waiting')
        : t('task.waitingOther', { name })
      : task.status === 'requested'
        ? t('task.requested', { name })
        : t(`task.${task.status}`, { name })
  const hint = !own || !waiting ? null : screen ? t(screen.online ? 'screen.waitingOn' : 'screen.closed', { name: screen.name }) : null
  const Icon = own ? IconDeviceTablet : IconDeviceMobile
  return (
    <div className={classes.status} data-state={waiting ? 'waiting' : 'ended'}>
      {waiting ? <Loader size={18} /> : <Icon size={20} />}
      <div className={classes.statusText}>
        <Text size="sm">{text}</Text>
        {hint && (
          <Text size="xs" c={screen?.online ? 'dimmed' : 'var(--tnn-tone-warning-fg)'}>
            {hint}
          </Text>
        )}
      </div>
      <Button variant="subtle" size="compact-sm" onClick={waiting ? handoff.cancel : handoff.clear}>
        {waiting ? t(own ? 'screen.doHere' : 'task.cancel') : t('task.dismiss')}
      </Button>
    </div>
  )
}

/** Asking someone else: whom (people with an account), and a word to them */
export function AskDialog({ opened, onClose, label, handoff }: { opened: boolean; onClose: () => void; label: string; handoff: Handoff }) {
  const { t } = useTranslation()
  const [person, setPerson] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (opened) {
      setPerson(null)
      setNote('')
      setError(undefined)
    }
  }, [opened])
  const load = useCallback(
    (q: string) =>
      tasksApi.people(q).then((items) => items.map((p) => ({ value: p.id, label: p.name, description: p.detail ?? undefined }))),
    [],
  )
  const resolve = useCallback(async () => [], [])
  const send = async () => {
    if (!person) return
    setBusy(true)
    try {
      await handoff.ask(person, note)
      onClose()
    } catch (err) {
      setError(isApiError(err) ? err.message : t('task.failed'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <ResponsiveDialog
      opened={opened}
      onClose={onClose}
      title={t('task.askTitle', { label })}
      size="md"
      footer={
        <>
          <Button variant="default" onClick={onClose}>
            {t('actions.cancel')}
          </Button>
          <Button onClick={send} loading={busy} disabled={!person}>
            {t('task.send')}
          </Button>
        </>
      }
    >
      <Stack gap="md">
        <RemoteSelect
          label={t('task.person')}
          placeholder={t('task.personPlaceholder')}
          value={person}
          onChange={setPerson}
          load={load}
          resolve={resolve}
          data-autofocus
        />
        <Textarea
          label={t('task.note')}
          autosize
          minRows={2}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        <Text size="xs" c="dimmed">
          {t('task.askHint')}
        </Text>
        {error && (
          <Text size="sm" c="var(--tnn-error)">
            {error}
          </Text>
        )}
      </Stack>
    </ResponsiveDialog>
  )
}
